import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import { useAppTranslation } from "locale/useAppTranslation";
import { TableSortState } from "components/table";
import { CustomerV1CustomerAddressDetail, useCustomerServiceV1GetCustomerDetailQuery } from "_services/modules/customer";
import { FlattenedAddress } from "_interfaces/customer.interfaces";


const useCustomerDetail = () => {
    const dispatch = useAppDispatch();
    const { t } = useAppTranslation("vendor");
    const { id = "" } = useParams();

    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<"fleet-list" | "branch-list" | "address-list">(
        "branch-list",
    );

    // Fleet list server-side pagination and sort states
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [sortState, setSortState] = useState<TableSortState>(null);

    // Branch list client-side pagination states
    const [branchPage, setBranchPage] = useState(1);
    const [branchRowsPerPage, setBranchRowsPerPage] = useState(5);

    // Address list client-side pagination states
    const [addressPage, setAddressPage] = useState(1);
    const [addressRowsPerPage, setAddressRowsPerPage] = useState(5);

    const { data: detail, isLoading: isDetailLoading } = useCustomerServiceV1GetCustomerDetailQuery({ id }, {
        skip: !id,
    });

    useEffect(() => {
        // @ts-expect-error temporary i18n key typing limitation
        const val = t("detail-page-title");
        const resolvedTitle = val === "detail-page-title" ? "Customer Detail" : val;
        dispatch(
            setGlobalComponent({
                title: resolvedTitle,
                hasBackButton: true,
            }),
        );
    }, [dispatch, t]);

    const flattenedAddresses = useMemo(() => {
        if (!detail?.branches) return [];
        const result: FlattenedAddress[] = [];
        detail.branches.forEach((branch) => {
            if (branch.addresses) {
                branch.addresses.forEach((address) => {
                    result.push({
                        ...address,
                        branch_name: branch.branch_name ?? "-",
                    });
                });
            }
        });
        return result;
    }, [detail]);

    const fleetRows = useMemo(() => [], []);

    // Client-side branch pagination
    const totalBranchPages = Math.ceil((detail?.branches?.length || 0) / branchRowsPerPage);
    const paginatedBranches = useMemo(() => {
        if (!detail?.branches) return [];
        const startIndex = (branchPage - 1) * branchRowsPerPage;
        return detail.branches.slice(startIndex, startIndex + branchRowsPerPage);
    }, [detail?.branches, branchPage, branchRowsPerPage]);

    // Client-side address pagination
    const totalAddressPages = Math.ceil(flattenedAddresses.length / addressRowsPerPage);
    const paginatedAddresses = useMemo(() => {
        const startIndex = (addressPage - 1) * addressRowsPerPage;
        return flattenedAddresses.slice(startIndex, startIndex + addressRowsPerPage);
    }, [flattenedAddresses, addressPage, addressRowsPerPage]);

    return {
        id,
        detail,
        flattenedAddresses,
        fleetRows,
        fleetTotalPages: 1,
        isHistoryOpen,
        setIsHistoryOpen,
        activeTab,
        setActiveTab,
        
        // Fleet pagination
        currentPage,
        setCurrentPage,
        rowsPerPage,
        setRowsPerPage,
        sortState,
        setSortState,
        
        // Branch pagination
        branchPage,
        setBranchPage,
        branchRowsPerPage,
        setBranchRowsPerPage,
        totalBranchPages,
        paginatedBranches,
        
        // Address pagination
        addressPage,
        setAddressPage,
        addressRowsPerPage,
        setAddressRowsPerPage,
        totalAddressPages,
        paginatedAddresses,
        
        isLoading: isDetailLoading,
        isFleetLoading: false,
        t,
    };
};

export default useCustomerDetail;
